'use client';

import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { ImagesModalSectionProps, MediaModalSectionName } from '@/features/media-modal/components/common/commonTypes';
import * as MediaAssetAPI from '@/api/media-asset';
import MediaGrid from '@/components/media-asset/MediaGrid';
import MediaAssetCard from '@/components/media-asset/MediaAssetCard';
import { useCurrentApp } from '@/stores/userDataStore';
import MediaAssetsLoader from '@/features/media-assets/components/MediaAssetsLoader';
import { ICardMediaAsset, MediaAssetType, MediaAssetStatus, StandaloneMediaAsset } from '@/types/media';
import { MediaAssetProviders } from '@/features/ai-video-creator/stores/productMediaSlice';
import { getOutputBucketUrl } from '@/utils/public-urls';
import { Box, Text, BlockStack, Card, InlineStack, Select, Button } from '@shopify/polaris';
import { NoContentButton } from './sections-content/NoContentButton';
import { useQueryClient } from '@tanstack/react-query';
import ProductFilter from '@/features/products/filters/ProductFilter';
import { Product } from '@/types/product';
import TemplateFilters from '@/features/studio-video-templates/TemplateFilters';
import { TemplateFeatures, TemplateName } from '@/features/studio-video-templates/types/template';
import { TEMPLATES } from '@/features/studio-video-templates/constants/template';

const creationTemplateFilterOptions = [
  {
    label: 'All',
    value: 'all',
  },
  ...TEMPLATES.map((template) => ({
    label: template.title,
    value: template.id,
  })),
];

const BunuVideosModalSection = ({
  selectedMedia,
  setSelectedMedia,
  setSelectedSection,
  multiSelection = true,
}: ImagesModalSectionProps) => {
  const { appKey } = useCurrentApp();
  const queryClient = useQueryClient();
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedFeatures, setSelectedFeatures] = useState<Set<TemplateFeatures>>(new Set());
  const [creationTemplateFilter, setCreationTemplateFilter] = useState<TemplateName | 'all'>('all');

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['video-assets', appKey] });
  }, [appKey, queryClient]);

  const handleSetCreationTemplateFilter = (template: TemplateName) => {
    setCreationTemplateFilter(template);
  };

  const handleProductChange = useCallback((product: Product | null) => {
    setSelectedProduct(product);
  }, []);

  const toggleFeature = useCallback((feature: TemplateFeatures) => {
    setSelectedFeatures((prev) => {
      const next = new Set(prev);
      if (next.has(feature)) {
        next.delete(feature);
      } else {
        next.add(feature);
      }
      return next;
    });
  }, []);

  const handleSelect = useCallback(
    (media: StandaloneMediaAsset, selected: boolean) => {
      const formattedMedia = {
        ...media,
        provider: MediaAssetProviders.aiGenerated,
        settings: media.type === MediaAssetType.video && media.durationMS ? { duration: media.durationMS } : undefined,
        src: `${getOutputBucketUrl()}/${media.result}`,
      };

      setSelectedMedia((prev) => {
        if (!multiSelection) {
          return selected ? [formattedMedia] : [];
        }

        return selected ? [...prev, formattedMedia] : prev.filter((item) => item.id !== media.id);
      });
    },
    [setSelectedMedia, multiSelection]
  );

  const loaderParams = {
    queryKey: ['video-assets', appKey],
    loaderFn: MediaAssetAPI.getAiVideos,
  };

  const handleEmptyStateAction = useCallback(() => {
    setSelectedSection(MediaModalSectionName.aiVideo);
  }, [setSelectedSection]);

  const renderItem = useCallback(
    (item) => {
      return (
        <MediaAssetCard
          key={item.id}
          media={item as ICardMediaAsset}
          size="small"
          hideMetadata
          hideUseAction
          selected={selectedMedia.some((media) => media.id === item.id)}
          onSelect={(media, selected) => handleSelect(media as StandaloneMediaAsset, selected)}
        />
      );
    },
    [handleSelect, selectedMedia]
  );

  const renderMediaAssets = useCallback(
    (mediaAssets: StandaloneMediaAsset[]) => {
      let filteredAssets = mediaAssets.filter(
        (asset) => asset.type === MediaAssetType.video && asset.status === MediaAssetStatus.DONE
      );

      if (selectedProduct) {
        filteredAssets = filteredAssets.filter(
          (asset) => asset.productId === selectedProduct.productId || asset.productId === selectedProduct.id
        );
      }

      if (creationTemplateFilter !== 'all') {
        filteredAssets = filteredAssets.filter((asset) => {
          const creationContext = asset.createdWith;
          return (
            creationContext && creationContext.toolName && creationContext.toolName.includes(creationTemplateFilter)
          );
        });
      }

      if (selectedFeatures.size > 0) {
        filteredAssets = filteredAssets.filter((asset) => {
          const creationContext = asset.createdWith;

          if (
            selectedFeatures.has(TemplateFeatures.Avatar) &&
            (!creationContext || !creationContext.toolName || !creationContext.toolName.includes('avatar'))
          ) {
            return false;
          }

          if (selectedFeatures.has(TemplateFeatures.ProductFocus) && !asset.productId) {
            return false;
          }

          if (selectedFeatures.has(TemplateFeatures.BulkCreation) && (!creationContext || !creationContext.labels)) {
            return false;
          }

          return true;
        });
      }

      if (!filteredAssets || filteredAssets.length === 0) {
        return (
          <div className="flex h-60 items-center justify-center">
            <Box background="bg-surface-secondary" padding="600" borderRadius="300">
              <BlockStack gap="400" align="center">
                <Text as="h2" variant="headingMd" alignment="center">
                  No videos found
                </Text>
                <Text as="p" variant="bodyMd" tone="subdued" alignment="center">
                  {selectedProduct
                    ? 'No AI videos found for this product. Try adjusting your filters.'
                    : 'No AI videos found matching your filters. Try adjusting filters.'}
                </Text>
              </BlockStack>
            </Box>
          </div>
        );
      }

      return <MediaGrid items={filteredAssets} size="medium" renderItem={renderItem} />;
    },
    [renderItem, handleEmptyStateAction, selectedProduct, selectedFeatures, creationTemplateFilter]
  );

  return (
    <div className="flex h-full flex-col">
      {/* Fixed header section */}
      <div className="flex-none">
        <BlockStack gap="400">
          <Box padding="200">
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Bunu videos
              </Text>

              <Card>
                <Box padding="300">
                  <BlockStack gap="400">
                    <InlineStack gap="400" blockAlign="center" wrap={false}>
                      <Box width="280px" padding="200">
                        <BlockStack gap="200">
                          <Text as="h3" variant="bodyMd" fontWeight="medium" tone="subdued">
                            Creation template
                          </Text>
                          <Select
                            label="Creation template"
                            labelHidden
                            options={creationTemplateFilterOptions}
                            value={creationTemplateFilter}
                            onChange={handleSetCreationTemplateFilter}
                          />
                        </BlockStack>
                      </Box>

                      <Box width="1px" paddingBlockStart="200" paddingBlockEnd="200">
                        <div className="bg-border-subdued h-full w-full"></div>
                      </Box>

                      <Box padding="200">
                        <ProductFilter selectedProduct={selectedProduct} onChange={handleProductChange} />
                      </Box>
                    </InlineStack>

                    <Box paddingInlineStart="200">
                      <TemplateFilters selectedFeatures={selectedFeatures} toggleFeature={toggleFeature} inline />
                    </Box>
                  </BlockStack>
                </Box>
              </Card>
            </BlockStack>
          </Box>
        </BlockStack>
      </div>
      <Box padding="200">
        <MediaAssetsLoader hideFailed {...loaderParams} renderMediaAssets={renderMediaAssets} size="medium" />
      </Box>
    </div>
  );
};

export default memo(BunuVideosModalSection);
